import React from 'react'

import {
  AlignItems,
  Box,
  Color,
  Display,
  FontWeight,
  Icon,
  IconNames,
  IconVariant,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'

type QuantityControlProps = {
  count: number
  decrementIcon: IconNames
  decrementLabel: string
  onAdd: () => void
  onRemove: () => void
  addLabel?: string
  addDisabled?: boolean
  addDataNoCode?: string
  countDataNoCode?: string
  width?: string
}

type QuantityControlButtonProps = {
  ariaLabel: string
  disabled?: boolean
  name: IconNames
  onClick: () => void
  dataNoCode?: string
}

const QuantityControlButton = ({
  ariaLabel,
  disabled = false,
  name,
  onClick,
  dataNoCode,
}: QuantityControlButtonProps) => (
  <Box
    display={Display.Flex}
    alignItems={AlignItems.Center}
    aria-disabled={disabled}
    aria-label={ariaLabel}
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={(event) => {
      event.stopPropagation()
      if (!disabled) onClick()
    }}
    onKeyDown={(event) => {
      if (disabled) return
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        event.stopPropagation()
        onClick()
      }
    }}
    style={{
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.35 : 1,
    }}
    data-no-code={dataNoCode}
  >
    <Icon name={name} variant={IconVariant.Actionable} />
  </Box>
)

export const QuantityControl = ({
  count,
  decrementIcon,
  decrementLabel,
  onAdd,
  onRemove,
  addLabel = 'Add item',
  addDisabled = false,
  addDataNoCode,
  countDataNoCode,
  width = 'auto',
}: QuantityControlProps) => (
  <Box
    bg={Color.NeutralGrey_50}
    style={{
      borderRadius: '60px',
      padding: '4px 9px',
    }}
    display={Display.Flex}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.SpaceBetween}
    gap={2}
    width={width}
  >
    <QuantityControlButton ariaLabel={decrementLabel} name={decrementIcon} onClick={onRemove} />
    <Text fontWeight={FontWeight.Bold} data-no-code={countDataNoCode}>
      {count}
    </Text>
    <QuantityControlButton
      name="add"
      ariaLabel={addLabel}
      onClick={onAdd}
      disabled={addDisabled}
      dataNoCode={addDataNoCode}
    />
  </Box>
)
